import { useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Tags } from 'lucide-react';

// Manage the shared job roles (scheduling Positions): rename or delete/archive unused ones,
// and create new ones. Kept in sync with the scheduler's role list.
export function StaffRolesManager({ allRoles, setAllRoles }) {
  const [newName, setNewName] = useState('');
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const { data } = await staffApi.post('/scheduling/positions', { name });
      setAllRoles((r) => [...r, data.position].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName('');
      toast.success(`Role "${name}" created`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const rename = async (r) => {
    const name = (drafts[r.id] ?? r.name).trim();
    if (!name || name === r.name) { setDrafts((d) => ({ ...d, [r.id]: undefined })); return; }
    try {
      const { data } = await staffApi.patch(`/scheduling/positions/${r.id}`, { name });
      setAllRoles((list) => list.map((x) => (x.id === r.id ? data.position : x)).sort((a, b) => a.name.localeCompare(b.name)));
      setDrafts((d) => ({ ...d, [r.id]: undefined }));
      toast.success('Role renamed');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete the role "${r.name}"? It will be removed from all staff. If it is used by existing shifts it will be hidden instead.`)) return;
    try {
      const { data } = await staffApi.delete(`/scheduling/positions/${r.id}`);
      setAllRoles((list) => list.filter((x) => x.id !== r.id));
      toast.success(data.archived ? 'Role archived (still referenced by past shifts)' : 'Role deleted');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="bg-white rounded-xl border border-border/40 p-5 space-y-4" data-testid="staff-roles-manager">
      <div className="flex items-center gap-2">
        <Tags size={16} className="text-saffron" />
        <h2 className="font-heading font-700 text-charcoal">Job Roles</h2>
      </div>
      <p className="text-xs text-muted-ink -mt-2">These roles are shared with the Scheduler. Assign them to staff from the Team tab.</p>

      <div className="flex items-center gap-2">
        <Input
          data-testid="role-new-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); create(); } }}
          placeholder="New role name (e.g. Cook, Cashier, Driver)"
        />
        <Button data-testid="role-create-btn" disabled={busy || !newName.trim()} onClick={create} className="bg-saffron hover:bg-saffron-hover gap-1 shrink-0">
          <Plus size={14} /> Add Role
        </Button>
      </div>

      <div className="divide-y divide-border/30 border border-border/40 rounded-lg">
        {allRoles.length === 0 && <p className="p-4 text-sm text-muted-ink">No roles yet — add your first one above.</p>}
        {allRoles.map((r) => {
          const draft = drafts[r.id];
          const dirty = draft !== undefined && draft.trim() !== r.name;
          return (
            <div key={r.id} className="flex items-center gap-2 p-2.5" data-testid="role-row">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.color || '#E8833A' }} />
              <Input
                data-testid={`role-name-input-${r.id}`}
                value={draft ?? r.name}
                onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                className="h-9 flex-1"
              />
              {dirty && (
                <Button data-testid={`role-save-btn-${r.id}`} size="sm" variant="outline" onClick={() => rename(r)} className="gap-1 shrink-0 text-coriander">
                  <Save size={14} /> Save
                </Button>
              )}
              <button data-testid={`role-delete-btn-${r.id}`} onClick={() => del(r)} className="text-destructive p-2 shrink-0" title="Delete role">
                <Trash2 size={15} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
